import { Component } from "react"

/* Cookie */
import Cookies from "js-cookie"

/* Redux */
import { connect } from "react-redux"

/* Helpers */
import { withRouter, utils } from "../helpers"

/* Constants */
import { sound, env } from "../constants"

/* REST API */
import { get } from "../api/Notification"
import { badge } from "../api/Support"
import { profile } from "../api/Profile"

/* Widgets */
import { Chat } from "../widgets/Support"
import { Profile as ProfileModal, Password, History } from "../widgets/Profile"
import { Notifications, Pause } from "../widgets/Notification"

/* Icons */
import { BellIcon, HeadsetIcon, ChevronDownIcon, UserIcon, LockIcon, ClockIcon, LogoutIcon } from "./Icons"

/* Sweet Alert */
import Swal from "../helpers/alert"

/* Translate */
import { t } from "../translates/translate"


/* Fields */
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

/* A COUNTER LONGER THAN THIS TURNS INTO 99+ */
const MAX_BADGE = 99


/* Component Header */
class Header extends Component {

    constructor() {
        super()

        /* The name of the login answer paints the chip until the profile arrives */
        const json = localStorage.getItem("user")
        const user = json ? JSON.parse(json) : null

        this.state = {
            visible: false,
            audioLoading: true,
            unread: 0,
            warning: false,
            supportBadge: 0,

            /* Profile */
            name: user && user.name ? user.name : "",
            image: null,
        }
    }

    componentDidMount = () => {
        this.socketEvents()
        this.load()
        this.badge()
        this.profile()
        document.addEventListener("click", this.handleClickOutside, true)
        document.addEventListener("keydown", this.handleKeyDown)
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { admin } = this.props

        /* The role arrives after the first render — the counter waits for it */
        if (prevProps.admin !== admin && this.canSupport()) {
            this.badge()
        }
    }

    componentWillUnmount = () => {
        document.removeEventListener("click", this.handleClickOutside, true)
        document.removeEventListener("keydown", this.handleKeyDown)
        this.removeSocketEvents()
    }

    /* Click Outside Handler */
    handleClickOutside = (event) => {

        /* The chip and the menu are one box — a click inside it is handled by the box itself */
        if (this._box && !this._box.contains(event.target)) {
            this.setState({ visible: false })
        }

        if (isIOS) {

            const { audioLoading } = this.state

            if (audioLoading) {

                sound.soundEffects.forEach((soundEffect) => {
                    soundEffect.audio.play()
                    soundEffect.audio.pause()
                })

                this.setState({ audioLoading: false })
            }
        }
    }

    /* Key Down Handler — Escape closes the menu */
    handleKeyDown = (event) => {

        if (event.key === "Escape") {
            this.setState({ visible: false })
        }
    }

    /* Load Notification Count */
    load = () => {
        get().then((response) => {
            if (response.status === 200) {
                this.setState({ unread: parseInt(utils.checkValue(response.data.unread, 0)) })
            }
        })
    }

    /* Load Profile — the chip shows the very same name and picture the profile keeps */
    profile = () => {
        profile().then((response) => {
            if (response.status === 200) {
                this.setState({
                    name: utils.checkValue(response.data.name, ""),
                    image: utils.checkValue(response.data.image, null),
                })
            }
        }).catch(() => {
            /* Skip — the name of the login answer stays */
        })
    }

    /* Support Access — only a superadmin, a manager and a pitboss answer players, a viewer just watches */
    canSupport = () => utils.can(this.props.admin, "edit")

    /* Load Badge */
    badge = () => {

        /* Check Access */
        if (!this.canSupport()) {
            return
        }

        badge().then((response) => {
            if (response.status === 200) {
                this.setState({ supportBadge: parseInt(response.data.unread) })
            }
        })
    }

    /* Handle Socket events */
    socketEvents = () => {

        /* Fields */
        const { socket } = this.props

        /* Pause event — the alert panel owns the sound, the bell just keeps pulsing */
        socket.on("pause", (data) => {

            /* Show The Alert */
            if (this._pause) {
                this._pause.push(data)
            }

            /* Paint The Bell */
            this.setState((state) => ({ warning: true, unread: state.unread + 1 }))
        })

        /* Support Event */
        socket.on("supportMessage", () => {
            this.badge()
        })
    }

    /* Open Notifications */
    goNotification = () => {

        /* Drop The Warning */
        this.setState({ warning: false })

        /* Open Panel */
        this.openModal(this._notifications)
    }

    /* Remove Socket events */
    removeSocketEvents = () => {
        const { socket } = this.props
        socket.off("pause")
        socket.off("supportMessage")
    }

    /* Open A Panel — the menu closes together with the click */
    openModal = (modal) => {

        /* Close Menu */
        this.setState({ visible: false })

        /* Open Panel */
        if (modal) {
            modal.open()
        }
    }

    /* Ask Before Leaving */
    askLogout = () => {

        /* Close Menu */
        this.setState({ visible: false })

        /* Ask */
        Swal.fire({
            title: t("header.logoutTitle"),
            text: t("header.logoutText"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            confirmButtonText: t("header.logout"),
            cancelButtonText: t("common.cancel"),
        }).then((result) => {
            if (result.isConfirmed) {
                this.logout()
            }
        })
    }

    /* Logout action */
    logout = () => {
        const { setToken, navigate } = this.props
        localStorage.removeItem("token")
        localStorage.removeItem("user")
        Cookies.remove("refresh")
        setToken(null)
        navigate("/")
    }


    /* INITIALS OF AN ADMIN WITHOUT A PICTURE */
    initials = () => {

        /* Fields */
        const { name } = this.state

        /* Check Name */
        if (name.trim().length === 0) {
            return "?"
        }

        /* Send Result */
        return name.trim().split(" ").filter((item) => item).slice(0, 2).map((item) => item.charAt(0).toUpperCase()).join("")
    }

    /* TITLE OF THE ROLE — state.admin.access is the only source of truth */
    role = () => t(`access.${utils.getRole(this.props.admin)}`)


    /* Draw Avatar */
    _avatar = (size = 34) => {

        /* Fields */
        const { image } = this.state

        /* Picture */
        if (image) {
            return <img src={`${env.mediapoint}${image}`} alt="Avatar" className="header--ava" style={{ width: size, height: size }} />
        }

        return <div className="header--ava initials" style={{ width: size, height: size }}>{this.initials()}</div>
    }

    /* Draw Notification */
    _notification = () => {

        /* Fields */
        const { warning, unread } = this.state

        return (
            <div onClick={() => this.goNotification()} className={`header--action ${warning ? "warning" : ""}`} title={t("header.notifications")}>
                <BellIcon size={18} />
                {parseInt(unread) > 0 && <div className="header--action-badge">{parseInt(unread) > MAX_BADGE ? "99+" : unread}</div>}
            </div>
        )
    }

    /* Draw Support */
    _support = () => {

        /* Fields */
        const { supportBadge } = this.state

        /* Check Access */
        if (!this.canSupport()) {
            return null
        }

        return (
            <div onClick={() => this._chat.open()} className="header--action" title={t("support.title")}>
                <HeadsetIcon size={18} />
                {supportBadge > 0 && <div className="header--action-badge">{supportBadge > MAX_BADGE ? "99+" : supportBadge}</div>}
            </div>
        )
    }

    /* Draw Menu */
    _menu = () => {

        /* Fields */
        const { visible, name } = this.state

        /* Fields */
        const items = [
            { icon: <UserIcon size={17} />, text: t("header.profile"), action: () => this.openModal(this._profile) },
            { icon: <LockIcon size={17} />, text: t("header.changePassword"), action: () => this.openModal(this._password) },
            { icon: <ClockIcon size={17} />, text: t("header.history"), action: () => this.openModal(this._history) },
        ]

        return (
            <div className={`header--menu ${visible ? "active" : ""}`}>

                <div className="header--menu-head">

                    {this._avatar(42)}

                    <div className="header--menu-head-text">
                        <b>{utils.checkValue(name, "—")}</b>
                        <span>{this.role()}</span>
                    </div>

                </div>

                <div className="header--menu-list">

                    {items.map((item, index) =>
                        <div onClick={item.action} className="header--menu-item" key={`${index}`}>
                            <div className="header--menu-item-icon">{item.icon}</div>
                            <span>{item.text}</span>
                        </div>
                    )}

                </div>

                <div onClick={() => this.askLogout()} className="header--menu-item danger">
                    <div className="header--menu-item-icon"><LogoutIcon size={17} /></div>
                    <span>{t("header.logout")}</span>
                </div>

            </div>
        )
    }

    /* Draw Panels */
    _modals = () => {

        /* Fields */
        const { socket, navigate } = this.props

        return (
            <>

                {/* Support Chat — a viewer never answers a player */}
                {this.canSupport() && <Chat ref={(ref) => this._chat = ref} socket={socket} updateBadge={() => this.badge()} />}

                {/* Notification Panels — the list and the alert of a paused game */}
                <Notifications ref={(ref) => this._notifications = ref} navigate={navigate} onUpdate={() => this.load()} />
                <Pause ref={(ref) => this._pause = ref} navigate={navigate} />

                {/* Profile Panels — every role reaches its own profile */}
                <ProfileModal ref={(ref) => this._profile = ref} onUpdate={() => this.profile()} />
                <Password ref={(ref) => this._password = ref} />
                <History ref={(ref) => this._history = ref} />

            </>
        )
    }

    render = () => {

        /* Fields */
        const { visible, name } = this.state

        return (
            <div className="header--wrapper">

                <div className="header">

                    {/* Draw Support */}
                    {this._support()}

                    {/* Draw Notification */}
                    {this._notification()}

                    <div ref={(ref) => this._box = ref} className="header--box">

                        {/* Draw User */}
                        <div onClick={() => this.setState({ visible: !visible })} className={`header--user ${visible ? "active" : ""}`}>

                            {this._avatar()}

                            <div className="header--user-text">
                                <div className="header--user-name">{utils.checkValue(name, "—")}</div>
                                <div className="header--user-role">{this.role()}</div>
                            </div>

                            <div className={`header--user-chevron ${visible ? "active" : ""}`}>
                                <ChevronDownIcon size={13} />
                            </div>

                        </div>

                        {/* Draw Menu */}
                        {this._menu()}

                    </div>

                </div>

                {/* Draw Panels */}
                {this._modals()}

            </div>
        )
    }

}

const mapStateToProps = (state) => {
    return {
        admin: state.admin,
        language: state.language,
    }
}

export default withRouter(connect(mapStateToProps)(Header))
